// src/components/termOfPayments/TermOfPaymentsTable.jsx
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { Stack, useTheme } from "@mui/material";

import useGetTermOfPayments from "../../hooks/termOfPayments/useGetTermOfPayments";
import useDeleteTermOfPayment from "../../hooks/termOfPayments/useDeleteTermOfPayment";
import useDeleteAllTermOfPayments from "../../hooks/termOfPayments/useDeleteAllTermOfPayments";
import useRestoreTermOfPayment from "../../hooks/termOfPayments/useRestoreTermOfPayment";
import useRestoreAllTermOfPayments from "../../hooks/termOfPayments/useRestoreAllTermOfPayments";
import useForceDeleteTermOfPayment from "../../hooks/termOfPayments/useForceDeleteTermOfPayment";
import useForceDeleteAllTermOfPayments from "../../hooks/termOfPayments/useForceDeleteAllTermOfPayments";

import useTableActions from "../../hooks/common/useTableActions";
import TableLayout from "../global/Table/TableLayout";
import TermOfPaymentsTableRow from "./Table/TermOfPaymentsTableRow";
import TermOfPaymentsTableColumns from "./Table/TermOfPaymentsTableColumns";
import ArchiveButton from "../global/Table/ArchiveButton";
import toast from "react-hot-toast";
import { usePermissions } from "../../auth/PermissionsContext";

const TermOfPaymentsTable = () => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng === "ar";
    const { has } = usePermissions();

    const perms = useMemo(
        () => ({
            list: has("termOfPayments_list"),
            create: has("termOfPayments_create"),
            edit: has("termOfPayments_edit"),
            delete: has("termOfPayments_delete"),
            forceDelete: has("termOfPayments_force_delete"),
            archiveList: has("termOfPayments_archive_list"),
            restore: has("termOfPayments_restore"),
            restoreAll: has("termOfPayments_restore-all-table"),
            deleteAll: has("termOfPayments_delete-all-table"),
            massForceDeleteAll: has("termOfPayments_mass_force_delete-all-table"),
        }),
        [has]
    );

    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    const isArchive = location.pathname.includes("/term-of-payments/archive");

    // CRUD Hooks
    const { deleteTermOfPayments, isDelete } = useDeleteTermOfPayment();
    const { deleteAllTermOfPaymentsMutation, isDeleteAll } = useDeleteAllTermOfPayments();
    const { restoreTermOfPayments, isRestore } = useRestoreTermOfPayment();
    const { restoreAllTermOfPaymentsMutation, isRestoreAll } = useRestoreAllTermOfPayments();
    const { forceDeleteTermOfPayments, isForceDelete } = useForceDeleteTermOfPayment();
    const { forceDeleteAllTermOfPaymentsMutation, isForceDeleteAll } = useForceDeleteAllTermOfPayments();

    // ✅ استخدام Custom Hook للإجراءات المشتركة
    const {
        page,
        perPage,
        search,
        sortBy,
        sortDir,
        selectedIds,
        menuAnchorEl,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleEdit,
        handleMenuDelete,
        handleMenuRestore,
        handleBulkDelete,
        handlePageChange,
        handlePerPageChange,
        setSelectedIds,
    } = useTableActions({
        basePath: "/term-of-payments",
        deleteSingle: deleteTermOfPayments,
        deleteAll: deleteAllTermOfPaymentsMutation,
        restoreSingle: restoreTermOfPayments,
        restoreAll: restoreAllTermOfPaymentsMutation,
        forceDeleteSingle: forceDeleteTermOfPayments,
        forceDeleteAll: forceDeleteAllTermOfPaymentsMutation,
        isArchive,
    });

    // ✨ Local State (specific to this table)
    const [statusFilter, setStatusFilter] = React.useState("");

    // ✨ Filters including status and sorting
    const filters = useMemo(
        () => ({
            search: search || undefined,
            status: statusFilter || undefined,
            sort_by: sortBy,
            sort_dir: sortDir,
            ...(isArchive ? { only_trashed: true } : {}),
        }),
        [search, statusFilter, sortBy, sortDir, isArchive]
    );

    // ✨ Filter configuration for TableFilterBar
    const filterConfig = useMemo(
        () => [
            {
                type: "select",
                name: "status",
                label: { ar: "الحالة", en: "Status" },
                options: [
                    { value: "", label: { ar: "الكل", en: "All" } },
                    { value: "active", label: { ar: "نشط", en: "Active" } },
                    { value: "disabled", label: { ar: "معطل", en: "Disabled" } },
                ],
                value: statusFilter,
                onChange: setStatusFilter,
            },
        ],
        [statusFilter]
    );

    // ✨ Clear all filters handler
    const handleClearAllFilters = () => {
        setStatusFilter("");
    };

    const { isLoading, error, termOfPayments, meta } = useGetTermOfPayments(
        page,
        perPage,
        filters
    );

    const items = useMemo(() => termOfPayments?.items || [], [termOfPayments]);

    const rowMenuDisabled = isArchive
        ? !perms.restore && !perms.forceDelete
        : !perms.edit && !perms.delete;

    const renderRow = (params) => (
        <TermOfPaymentsTableRow
            key={params.item.id}
            termOfPayment={params.item}
            index={params.index}
            isDark={isDark}
            page={params.page}
            perPage={params.perPage}
            sortBy={params.sortBy}
            sortDir={params.sortDir}
            totalItems={items.length}
            selectedIds={selectedIds}
            onRowCheckboxClick={handleRowCheckboxClick}
            onMenuOpen={handleMenuOpen}
            isArchive={isArchive}
            menuDisabled={rowMenuDisabled}
        />
    );

    const bulkDeleteDisabled = isArchive
        ? !perms.massForceDeleteAll
        : !perms.deleteAll;

    const title = isArabic ? "شروط الدفع" : "Term of Payments";
    const subtitle = isArchive
        ? isArabic
            ? "عرض شروط الدفع المحذوفة (الأرشيف)"
            : "Archived term of payments (soft deleted)"
        : isArabic
            ? "إدارة شروط الدفع المتاحة في النظام"
            : "Manage available term of payments in the system";

    const addButtonDisabled = isArchive
        ? !perms.restoreAll || selectedIds.length === 0
        : !perms.create;

    const addButtonLabel = isArchive
        ? isRestoreAll
            ? isArabic
                ? "جاري الاستعادة..."
                : "Restoring..."
            : isArabic
                ? "استعادة المحدد"
                : "Restore selected"
        : isArabic
            ? "إضافة شرط دفع"
            : "Create new term of payment";

    const addButtonAction = isArchive
        ? () => {
            if (addButtonDisabled) {
                toast.error(
                    isArabic
                        ? "ليس لديك صلاحية للاستعادة"
                        : "No restore permission"
                );
                return;
            }

            if (isRestoreAll) return;

            if (!selectedIds.length) {
                toast.error(
                    isArabic
                        ? "الرجاء تحديد العناصر المراد استرجاعها أولاً"
                        : "Please select items to restore first"
                );
                return;
            }

            restoreAllTermOfPaymentsMutation({ items: selectedIds });
            setSelectedIds([]);
        }
        : () => {
            if (addButtonDisabled) {
                toast.error(
                    isArabic
                        ? "ليس لديك صلاحية للإنشاء"
                        : "No create permission"
                );
                return;
            }

            navigate("/term-of-payments/create");
        };

    return (
        <>
            {!isArchive && (
                <Stack
                    direction="row"
                    justifyContent={isArabic ? "flex-start" : "flex-end"}
                    sx={{ mb: 1 }}
                >
                    <ArchiveButton
                        to="/term-of-payments/archive"
                        visible={perms.archiveList}
                        disabled={!perms.archiveList}
                        archiveLabelAr="الأرشيف"
                        archiveLabelEn="Archive"
                    />
                </Stack>
            )}
            {isArchive && (
                <Stack
                    direction="row"
                    justifyContent={isArabic ? "flex-start" : "flex-end"}
                    sx={{ mb: 1 }}
                >
                    <ArchiveButton
                        to="/term-of-payments"
                        visible={true}
                        archiveLabelAr="العودة للقائمة"
                        archiveLabelEn="Back to list"
                    />
                </Stack>
            )}
            <TableLayout
                title={title}
                subtitle={subtitle}
                search={search}
                onSearchChange={setSearch}
                addButtonLabel={addButtonLabel}
                onAddClick={addButtonAction}
                addButtonVisible={true}
                isLoading={isLoading}
                error={error}
                items={items}
                meta={meta}
                page={page}
                perPage={perPage}
                onPageChange={handlePageChange}
                onPerPageChange={handlePerPageChange}
                sortBy={sortBy}
                sortDir={sortDir}
                onRequestSort={handleRequestSort}
                columns={TermOfPaymentsTableColumns}
                renderRow={renderRow}
                menuAnchorEl={menuAnchorEl}
                openMenu={Boolean(menuAnchorEl)}
                onMenuOpen={handleMenuOpen}
                onMenuClose={handleMenuClose}
                onMenuDelete={handleMenuDelete}
                onMenuRestore={isArchive ? handleMenuRestore : undefined}
                onMenuEdit={!isArchive ? handleEdit : undefined}
                menuDisableEdit={!isArchive && !perms.edit}
                menuDisableRestore={isArchive && !perms.restore}
                menuDisableDelete={isArchive ? !perms.forceDelete : !perms.delete}
                isDeletingSingle={isArchive ? isRestore || isForceDelete : isDelete}
                selectedIds={selectedIds}
                onSelectAllClick={(e) => handleSelectAllClick(e, items)}
                onRowCheckboxClick={handleRowCheckboxClick}
                onBulkDelete={handleBulkDelete}
                bulkDeleteDisabled={bulkDeleteDisabled}
                isBulkDeleting={isArchive ? isForceDeleteAll : isDeleteAll}
                addButtonDisabled={addButtonDisabled}
                addButtonTooltip={
                    addButtonDisabled
                        ? isArabic
                            ? "ليس لديك صلاحية"
                            : "No permission"
                        : ""
                }
                filterConfig={filterConfig}
                onClearAllFilters={handleClearAllFilters}
                hasFilters={!!statusFilter}
            />
        </>
    );
};

export default TermOfPaymentsTable;